import React from "react";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import PageLayout from "../components/page-layout/PageLayout";
import SEO from "../components/SEO";
import { HeroCta } from "../components/cta/HeroCta";
import GridBlock from "../components/grid-block/GridBlock";
import Keyword from "../components/typography/Keyword";
import { useServiceTree } from "../hooks/useServiceTree";
import KeywordLink from "../components/typography/KeywordLink";
import { useServiceI18nLink, useUseCaseI18nLink } from "../hooks/useI18nLink";
import Experts from "../components/sections/Experts";
import ImageCta from "../components/cta/ImageCta";
import TechIcon from "../components/icons/TechIcon";
import ImageTextSection from "../components/text/ImageTextSection";
import { FeaturesGrid3 } from "../components/features-grid/FeaturesGrid3";
import SideTextSection from "../components/text/SideTextSection";
import ImageHero4 from "../components/hero/ImageHero4";
import HtmlTrans from "../components/typography/HtmlTrans";
import TextHero from "../components/hero/TextHero";
import SideGridBlock from "../components/grid-block/SideGridBlock";
import { useI18next } from "gatsby-plugin-react-i18next";
import { useServiceUseCases } from "../hooks/useUseCases";
import { LinkCta } from "../components/typography/LinkCta";
import { FeaturesGrid6 } from "../components/features-grid/FeaturesGrid6";
import { MethodCta } from "../components/cta/MethodCta";
import { Faq } from "../components/FAQ";
import { TestimonialsTopCta } from "../components/cta/TestimonialsCta";

interface Solution {
  id: string;
  slug: string;
  serviceId: string;
  datePublished: string;
  dateModified: string;
  techs: string[];
  experts: string[];
  image: string;
}

interface SolutionTemplateProps {
  solution: Solution;
  experts: {
    name: string;
    picture: string;
    role: string;
    linkedinUrl: string;
  }[];
}

const getImage = (image: string | undefined) => {
  if (!image) return undefined;

  return require(`../assets/images/solutions/${image}`).default;
};

const SolutionTemplate: FunctionComponent<SolutionTemplateProps> = ({
  solution,
  experts,
}) => {
  const { t, i18n } = useTranslation();
  const services = useServiceTree();
  const { serviceLink } = useServiceI18nLink();
  const { language } = useI18next();
  const { useCaseLink } = useUseCaseI18nLink();
  const useCases = useServiceUseCases(solution.id);

  const image = getImage(solution.image);

  const otherService = services.filter((s) => s.id !== solution.serviceId);
  const targets: Record<string, string> = t(
    `solutions:${solution.id}.targets.list`,
    { returnObjects: true },
  );

  const benefits = i18n.exists(`solutions:${solution.id}.benefits`)
    ? (
        t(`solutions:${solution.id}.benefits.list`, {
          returnObjects: true,
        }) as {
          icon: string;
          name: string;
          description: string;
        }[]
      ).map((benefit) => {
        return {
          icon: benefit.icon,
          name: benefit.name,
          description: <HtmlTrans i18nKey={benefit.description} />,
        };
      })
    : null;

  const solutions = i18n.exists(`solutions:${solution.id}.solutions`)
    ? (
        t(`solutions:${solution.id}.solutions.list`, {
          returnObjects: true,
        }) as {
          icon: string;
          name: string;
          description: string;
        }[]
      ).map((benefit) => {
        return {
          icon: benefit.icon,
          name: benefit.name,
          description: <HtmlTrans i18nKey={benefit.description} />,
        };
      })
    : null;

  return (
    <PageLayout pageName={solution.slug} darkHeader>
      <SEO
        titleI18nKey={t(`solutions:${solution.id}.meta.title`)!}
        descriptionI18nKey={t(`solutions:${solution.id}.meta.description`)!}
        image={image}
        datePublished={new Date(solution.datePublished)}
        dateModified={new Date(solution.dateModified)}
        service={t(`solutions:${solution.id}.meta.schema`, {
          returnObjects: true,
          defaultValue: null,
        })}
      />

      {/* HERO */}
      {solution.image ? (
        <ImageHero4
          dark
          tag={t(`solutions:${solution.id}.tag`)}
          title={<HtmlTrans i18nKey={t(`solutions:${solution.id}.title`)} />}
          description={
            <HtmlTrans i18nKey={`solutions:${solution.id}.description`} />
          }
          topCta={<TestimonialsTopCta theme="dark" />}
          cta={<HeroCta methodCta theme="dark" />}
          image={image}
        />
      ) : (
        <TextHero
          dark
          wide
          tag={t(`solutions:${solution.id}.tag`)}
          title={<HtmlTrans i18nKey={t(`solutions:${solution.id}.title`)} />}
          description={
            <HtmlTrans i18nKey={`solutions:${solution.id}.description`} />
          }
          topCta={<TestimonialsTopCta theme="dark" className="mx-auto" />}
          cta={<HeroCta methodCta theme="dark" center />}
        />
      )}

      {/* HOW IT WORKS */}
      <ImageTextSection
        tag={t(`solutions:${solution.id}.howItWorks.tag`)}
        title={t(`solutions:${solution.id}.howItWorks.title`)}
        text={
          <HtmlTrans i18nKey={`solutions:${solution.id}.howItWorks.text`} />
        }
        image={require(`../assets/images/${solution.id}.jpg`).default}
        theme="dark"
      >
        <HeroCta />
      </ImageTextSection>

      {/* TARGET */}
      <GridBlock
        tag={t(`solutions:${solution.id}.targets.tag`)}
        title={t(`solutions:${solution.id}.targets.title`)}
        description={
          i18n.exists(`solutions:${solution.id}.targets.description`) ? (
            <HtmlTrans
              i18nKey={`services:${solution.id}.targets.description`}
            />
          ) : (
            ""
          )
        }
        lg3={false}
        theme="white"
        noMargin="top"
        blocks={Object.keys(targets).map((target) => (
          <Keyword
            title={target}
            description={<HtmlTrans i18nKey={targets[target]} />}
          />
        ))}
      />

      {/* VANTAGGI */}
      {benefits && (
        <FeaturesGrid6
          tag={t(`solutions:${solution.id}.benefits.tag`)}
          title={t(`solutions:${solution.id}.benefits.title`)}
          description={
            i18n.exists(`solutions:${solution.id}.benefits.description`) && (
              <HtmlTrans
                i18nKey={`services:${solution.id}.benefits.description`}
              />
            )
          }
          elements={benefits}
          theme="gray"
        />
      )}

      {/* ADDITIONAL TEXT */}
      {i18n.exists(`solutions:${solution.id}.info`) && (
        <SideTextSection
          tag={t(`solution:${solution.id}.info.tag`)}
          title={
            <HtmlTrans i18nKey={t(`solution:${solution.id}.info.title`)} />
          }
          text={<HtmlTrans i18nKey={`solution:${solution.id}.info.text`} />}
        />
      )}

      {/* CTA */}
      <ImageCta
        tag={t(`solution:${solution.id}.pageCta.tag`)}
        title={t(`solution:${solution.id}.pageCta.title`)}
        description={
          <HtmlTrans
            i18nKey={t(`solution:${solution.id}.pageCta.description`)}
          />
        }
        image={require("../assets/images/astrorei-cta.jpg").default}
      />

      {/* VANTAGGI */}
      {solutions && (
        <FeaturesGrid6
          tag={t(`solutions:${solution.id}.solutions.tag`)}
          title={t(`solutions:${solution.id}.solutions.title`)}
          description={
            i18n.exists(`solutions:${solution.id}.solutions.description`) && (
              <HtmlTrans
                i18nKey={`services:${solution.id}.solutions.description`}
              />
            )
          }
          elements={solutions}
          theme="white"
        />
      )}

      {/* TECHS */}
      {solution.techs && (
        <SideGridBlock
          tag={t(`solution:${solution.id}.tech.tag`)}
          title={
            <HtmlTrans i18nKey={t(`solution:${solution.id}.tech.title`)} />
          }
          description={
            <HtmlTrans
              i18nKey={t(`solution:${solution.id}.tech.description`)}
            />
          }
          theme="gray"
          mobileMultiCol
          // image={<ConsoleBox tech={"react"} />}
          blocks={solution.techs.map((tech) => (
            <div className="flex items-center justify-center h-24 w-full">
              <TechIcon
                icon={tech}
                size="large"
                caption={tech}
                className="text-gray-800 dark:text-gray-200 z-10"
              />
            </div>
          ))}
        />
      )}

      {/* FAQS */}
      {i18n.exists(`solutions:${solution.id}.faqs`) && (
        <Faq
          theme="indigo"
          tag={t(`solution:${solution.id}.faqs.tag`)}
          title={t(`solution:${solution.id}.faqs.title`)}
          description={
            i18n.exists(`solutions:${solution.id}.faqs.description`)
              ? t(`solution:${solution.id}.faqs.description`)
              : undefined
          }
          faqs={t(`solution:${solution.id}.faqs.faqs`, { returnObjects: true })}
        />
      )}

      {/* EXPERTS */}
      {solution.experts && (
        <Experts
          tag={t("solutions:team.tag")}
          title={t("solutions:team.title")}
          experts={experts.filter((exp) => solution.experts.includes(exp.name))}
        />
      )}

      {/* USE CASES */}
      {useCases && useCases.length > 0 && (
        <GridBlock
          tag={t(`index:useCase.tag`)}
          title={t(`index:useCase.title`)}
          description={t(`index:useCase.description`)}
          lg3={false}
          theme="gray"
          blocks={useCases.slice(0, 4).map((useCase) => (
            <>
              <Keyword
                title={useCase.hero.projectName[language]}
                description={useCase.hero.projectName[language]}
              />
              <LinkCta
                to={useCaseLink(useCase)}
                text={t("index:useCase.ctaLink")}
                align="right"
              />
            </>
          ))}
        />
      )}

      {/* OTHER SOLUTIONS */}
      {!solutions && (
        <FeaturesGrid3
          tag={t(`services:${solution.serviceId}.solutions.tag`)}
          title={t(`services:${solution.serviceId}.solutions.title`)}
          theme="white"
          elements={services
            .find((s) => s.id === solution.serviceId)!
            .solutions.filter((s) => s.id !== solution.id)
            .map((solution) => ({
              name: t(`solutions:${solution.id}.name`),
              // description: t(`solutions:${solution.id}.description`),
              to: serviceLink(solution),
              icon: (
                <TechIcon
                  size="xl"
                  icon={solution.icon}
                  className="text-gray-900 dark:text-gray-100 overflow-hidden"
                />
              ),
            }))}
        />
      )}

      {/* OTHER SERVICES */}
      <GridBlock
        tag={t(`services:otherService.tag`)}
        title={t(`services:otherService.title`)}
        lg3={false}
        noMargin="bottom"
        blocks={otherService.map((s) => (
          <div className="py-6">
            <KeywordLink
              title={t(`services:${s.id}.name`)}
              to={serviceLink(s)}
            />
          </div>
        ))}
      />
    </PageLayout>
  );
};

export default SolutionTemplate;
